import { Button, downloadBlob, TextInput } from "@hoylu/client-common";
import { DocumentService } from "@hoylu/web-io-document";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import type { RootState } from "typesafe-actions";
import {
  documentMetadata,
  transferService,
} from "../../../state/config/config.selector";
import {
  authorizationHeader,
  userToken,
} from "../../../state/user/user.selector";
import { cancelDashboardOption } from "../../../state/workspaces/workspaces.actions";
import { selectSelectedWorkspace } from "../../../state/workspaces/workspaces.selector";
import { useI18n } from "../../../utils/hooks/use.i18n";
import { useTextInputLocalization } from "../../../utils/useTextInputLocalization";
import { Modal } from "../Modal";
import Styles from "./DownloadDialog.module.css";

const mapStateToProps = (state: RootState) => ({
  authHeader: authorizationHeader(state),
  documentMetadataUrl: documentMetadata(state),
  fetchApi: window.fetch,
  saveFile: downloadBlob,
  selectedMyWorkspaceMetadata: selectSelectedWorkspace(state),
  token: userToken(state),
  transferServiceUrl: transferService(state),
});

const mapDispatchToProps = {
  cancelDashboardOption,
};

export type DownloadDialogProps = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const DownloadDialog: React.FC<DownloadDialogProps> = ({
  authHeader,
  cancelDashboardOption,
  documentMetadataUrl,
  fetchApi,
  saveFile,
  selectedMyWorkspaceMetadata,
  token,
  transferServiceUrl,
}) => {
  const tDownloadDialog = useI18n("DOWNLOAD_DIALOG.");
  const localizationDict = useTextInputLocalization();

  const isPasswordRequired = !!selectedMyWorkspaceMetadata?.hasPassword;

  const [downloadButtonDisabled, setDownloadButtonDisabled] =
    useState(isPasswordRequired);
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [timestamp, setTimestamp] = useState<string | undefined>();

  const abortControllerRef = useRef<AbortController | null>(null);
  const isActive = useRef(true);

  useEffect(() => {
    return () => {
      isActive.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  if (!selectedMyWorkspaceMetadata) return null;

  // future improvements
  // https://stackoverflow.com/a/72914817
  //
  // use https://developer.mozilla.org/en-US/docs/Web/API/Window/showSaveFilePicker
  // and https://developer.mozilla.org/en-US/docs/Web/API/FileSystemWritableFileStream
  // or
  // https://github.com/jimmywarting/native-file-system-adapter

  // possible workaround https://stackoverflow.com/a/72576242
  const download = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    abortControllerRef.current = new AbortController();

    setDownloadButtonDisabled(true);
    setMessage(tDownloadDialog("DOWNLOADING"));

    let url = `${transferServiceUrl}/Download/${selectedMyWorkspaceMetadata.workspaceId}`;
    if (timestamp) {
      const dateTime = new Date(timestamp);
      url += `?timestamp=${dateTime.toISOString()}`;
    }

    try {
      const service = new DocumentService({
        server: documentMetadataUrl,
        credentialProvider: () => ({ token }),
      });
      await service.connect(
        selectedMyWorkspaceMetadata.workspaceId,
        isPasswordRequired ? password : undefined,
      );

      const response = await fetchApi(url, {
        method: "GET",
        mode: "cors",
        headers: authHeader,
        signal: abortControllerRef.current.signal,
      });
      if (response.ok) {
        const filename =
          response.headers
            .get("content-disposition")
            ?.split(";")
            .find((n) => n.includes("filename="))
            ?.replace("filename=", "")
            .trim()
            .replace(/^"(.*)"$/, "$1") ||
          `${selectedMyWorkspaceMetadata.workspaceId}.zip`;
        const blob = await response.blob();
        saveFile(blob, filename);
        cancelDashboardOption();
      } else {
        if (isActive.current) {
          setMessage(tDownloadDialog("DOWNLOAD_FAILED"));
          setDownloadButtonDisabled(false);
        }
        abortControllerRef.current = null; // Should this be moved to a finally block?
        console.warn(response);
      }
    } catch (error) {
      if (isActive.current) {
        if (error?.errorCode === 403) {
          setMessage(tDownloadDialog("DOWNLOAD_FAILED_403"));
        } else {
          setMessage(tDownloadDialog("DOWNLOAD_FAILED"));
        }
        setDownloadButtonDisabled(false);
      }
      abortControllerRef.current = null;
      console.warn(error);
    }
  };

  return (
    <Modal handleOutsideClick={cancelDashboardOption}>
      <h1 className={Styles.header} id="download-dialog-label">
        {tDownloadDialog("DOWNLOAD")}
      </h1>

      <form onSubmit={download}>
        {isPasswordRequired && (
          <TextInput
            classNames={[Styles.passwordPrompt]}
            isFocus
            label={tDownloadDialog("PASSWORD")}
            localizationDict={localizationDict}
            name="password"
            onChange={(value) => {
              setPassword(value);
              setDownloadButtonDisabled(!value);
            }}
            type="password"
            useTogglePassword
          />
        )}
        {message}
        <TextInput
          label={tDownloadDialog("TIMESTAMP_LABEL")}
          onChange={(date) => setTimestamp(date)}
          toolTip={tDownloadDialog("TIMESTAMP_TOOLTIP")}
          type="datetime-local"
        />
        <footer className={Styles.footer}>
          <Button
            formType="button"
            onClick={cancelDashboardOption}
            toolTip={tDownloadDialog("CANCEL")}
            type="negative"
          >
            {tDownloadDialog("CANCEL")}
          </Button>
          <Button
            disabled={downloadButtonDisabled}
            formType="submit"
            toolTip={tDownloadDialog("DOWNLOAD")}
            type="primary"
          >
            {tDownloadDialog("DOWNLOAD")}
          </Button>
        </footer>
      </form>
    </Modal>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(DownloadDialog);
